import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Target, ListTodo, CalendarDays, CalendarCheck, Users, BarChart3, Plus, FileText } from "lucide-react";
import API from "@/lib/api";

const pages = [
  { label: "Goals", path: "/", icon: Target },
  { label: "Projects", path: "/all-projects", icon: ListTodo },
  { label: "All Tasks", path: "/flat-view", icon: ListTodo },
  { label: "Weekly Roster", path: "/weekly-roster", icon: CalendarDays },
  { label: "Daily Roster", path: "/daily-roster", icon: CalendarCheck },
  { label: "People", path: "/people", icon: Users },
  { label: "Analytics", path: "/analytics", icon: BarChart3 },
];

function getMonday() { const d = new Date(); d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1)); return [d.getFullYear(),String(d.getMonth()+1).padStart(2,'0'),String(d.getDate()).padStart(2,'0')].join('-'); }
function todayStr() { const d = new Date(); return [d.getFullYear(),String(d.getMonth()+1).padStart(2,'0'),String(d.getDate()).padStart(2,'0')].join('-'); }

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => { if (e.key === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen(prev => !prev); } };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      try { const res = await API.get("/tasks/search?q=" + encodeURIComponent(query)); setSearchResults(res.data.slice(0, 8)); }
      catch { setSearchResults([]); }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const run = useCallback((cmd) => { setOpen(false); setQuery(""); setSearchResults([]); cmd(); }, []);

  // Filter pages locally
  const filteredPages = query.length > 0
    ? pages.filter(p => p.label.toLowerCase().includes(query.toLowerCase()))
    : pages;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setQuery(""); setSearchResults([]); } }}>
      <DialogContent className="overflow-hidden p-0 max-w-[520px]">
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5">
      <CommandInput placeholder="Search tasks, notes, or navigate..." value={query} onValueChange={setQuery} />
      <CommandList>
        {query.length >= 2 && searchResults.length === 0 && filteredPages.length === 0 && (
          <CommandEmpty>No results found.</CommandEmpty>
        )}
        {searchResults.length > 0 && (
          <CommandGroup heading="Tasks">
            {searchResults.map(t => (
              <CommandItem key={t.task_id} value={"task-" + t.task_id} onSelect={() => run(() => navigate("/tasks/" + t.task_id))}>
                <FileText className="mr-2 h-4 w-4 text-[#6B6B6B]" />
                <span className="flex-1">{t.title}</span>
                {t._matched_in_notes && <span className="text-[10px] text-[#6B6B6B] ml-2">(in notes)</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {filteredPages.length > 0 && (
          <CommandGroup heading="Navigate">
            {filteredPages.map(p => (
              <CommandItem key={p.path} value={"nav-" + p.label} onSelect={() => run(() => navigate(p.path))}>
                <p.icon className="mr-2 h-4 w-4 text-[#6B6B6B]" />
                <span>{p.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(!query || query.length < 2) && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Actions">
              <CommandItem value="action-import-weekly" onSelect={() => run(() => navigate("/roster-import?target=weekly&week_start=" + getMonday()))}>
                <Plus className="mr-2 h-4 w-4 text-[#6B6B6B]" /><span>Import to Weekly Roster</span>
              </CommandItem>
              <CommandItem value="action-import-daily" onSelect={() => run(() => navigate("/roster-import?target=daily&date=" + todayStr() + "&source=all"))}>
                <Plus className="mr-2 h-4 w-4 text-[#6B6B6B]" /><span>Import to Today's Roster</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
